/**
 * The access and visibility rules of the person modal, in one testable place.
 *
 * These decisions used to be inline expressions in OverviewTab/index JSX — including the
 * CPF edit gate, which is a privacy rule. Inline, nothing could assert them and a
 * refactor could silently widen access.
 *
 * NOTE these are UI gates only. The backend enforces the real thing; a rule here deciding
 * "true" must never be read as authorization.
 */

import {
  formatLiberacaoDate,
  liberacaoAppointmentLabel,
  liberacaoStatusForRole,
  liberacaoSummaryForRole,
} from '../../utils/liberacoes';
import { hasMinimumRoleLevel, isAdmin, isManager } from '../../config/roles';
import { canViewFullCPF } from '../../utils/cpfFormatter';

/** Role level of porteiro — the floor for staff-ish actions. */
const STAFF_LEVEL = 60;
/** Role level of admin — the floor for identity/user-account actions. */
const ADMIN_LEVEL = 90;

export interface PersonRoleLike {
  role_type?: string | null;
  is_active?: boolean | null;
}

/**
 * The role set a gate should consider: customer-scoped roles plus the global realm roles.
 *
 * Roles are stored in two places (user_customer_assignments and the Keycloak realm) and a
 * given deployment may use either, so a gate that reads only one locks out legitimate
 * users. Deduplicated so callers can count or compare without surprises.
 */
export function effectivePersonRoles(
  customerRoles?: string[] | null,
  globalRoles?: string[] | null,
): string[] {
  return Array.from(new Set([...(customerRoles ?? []), ...(globalRoles ?? [])]));
}

/**
 * Who may change a person's CPF. Admins always may. A porteiro may only when the
 * condomínio turned `porteiro_can_edit_cpf` on — and even then they still cannot SEE the
 * stored value, which is a separate rule (canViewFullCPF).
 */
export function canEditCPF(effectiveRoles: string[], porteiroCPFEnabled: boolean): boolean {
  return canViewFullCPF(effectiveRoles) || (porteiroCPFEnabled && hasMinimumRoleLevel(effectiveRoles, STAFF_LEVEL));
}

/**
 * Whether to tell this operator, on the CPF field, that the required mark does not bind them.
 *
 * The label renders a red `*` by default, and for a manager that is only half true: neither this
 * screen nor the server refuses a blank CPF from someone who edits the field in place, so the
 * asterisk promises an enforcement that does not exist. Shown only to the people it is true for —
 * a porteiro with `porteiro_can_edit_cpf` may REPLACE a CPF but not blank one (see cpfToSubmit),
 * and a read-only viewer may do neither.
 */
export function showsCpfBypassHint(input: {
  required: boolean;
  canEdit: boolean;
  canViewFull: boolean;
}): boolean {
  return input.required && input.canEdit && input.canViewFull;
}

/** Who may delete a person: staff (porteiro) and above. */
export function canDeletePerson(roles: string[]): boolean {
  return hasMinimumRoleLevel(roles, STAFF_LEVEL);
}

/** Who may link/unlink the Keycloak user account: admins only. */
export function canManageUserLinking(roles: string[]): boolean {
  return hasMinimumRoleLevel(roles, ADMIN_LEVEL);
}

/** Who may replace identity/profile photos: admins and managers (síndico, supervisor). */
export function canEditPhotos(roles: string[]): boolean {
  return isAdmin(roles) || isManager(roles);
}

/**
 * The modal's effective read-only state. A caller can open the modal read-only, but an
 * admin or manager keeps the ability to fix a photo — that override is deliberate.
 */
export function resolveReadOnly(readOnly: boolean, roles: string[]): boolean {
  return readOnly && !canEditPhotos(roles);
}

/** Every ACTIVE role type a person holds. A person can legitimately hold several. */
export function activeRoleTypes(roles?: PersonRoleLike[] | null): string[] {
  return (roles ?? [])
    .filter((r) => r?.is_active)
    .map((r) => r?.role_type ?? '')
    .filter(Boolean);
}

/** Role types for which "Nova Visita" makes sense. */
const VISIT_CAPABLE_ROLES = ['visitor', 'service_provider'];

/**
 * Whether the "Nova Visita" button belongs on this person: they are a visitor or
 * prestador, or they have no active role yet (a fresh record that a visit would define).
 *
 * Deliberately asks about ALL active roles. The previous implementation read
 * `roles.find(r => r.is_active)` — ONE arbitrary role — so a person holding both an
 * active resident role and an active visitor role got a different answer depending on the
 * order the API happened to return, and it disagreed with the sibling effect that fetches
 * visitor stats via `roles.some(...)`.
 */
export function shouldShowNovaVisita(roles?: PersonRoleLike[] | null): boolean {
  const active = activeRoleTypes(roles);
  if (active.length === 0) return true;
  return active.some((roleType) => VISIT_CAPABLE_ROLES.includes(roleType));
}

/**
 * Role type -> the ASO category a customer's `aso_applies_to` list speaks in. A role type
 * missing from this map belongs to no category and so never enables the tab.
 */
const ROLE_TYPE_TO_ASO_CATEGORY: Record<string, string> = {
  resident: 'moradores',
  staff: 'funcionarios',
  employee: 'funcionarios',
  condo_employee: 'funcionarios',
  service_provider: 'prestadores',
  visitor: 'visitantes',
};

export interface ASOCustomerLike {
  condo_data?: {
    aso_enabled?: boolean | null;
    aso_applies_to?: string[] | null;
  } | null;
}

/**
 * Whether the ASO (occupational health exam) tab applies to this person.
 *
 * Two conditions, both required: the customer has ASO switched on, and at least one of
 * the person's roles falls into a category the customer applies ASO to. Deliberately
 * considers ALL roles, not only active ones — an exam record outlives the role that
 * required it, and hiding the tab would hide the history.
 */
export function shouldShowASOTab(
  customer?: ASOCustomerLike | null,
  roles?: PersonRoleLike[] | null,
): boolean {
  if (!customer?.condo_data?.aso_enabled) return false;

  const appliesTo = customer.condo_data.aso_applies_to ?? [];
  if (appliesTo.length === 0) return false;

  return (roles ?? [])
    .map((r) => ROLE_TYPE_TO_ASO_CATEGORY[r?.role_type ?? ''])
    .filter(Boolean)
    .some((category) => appliesTo.includes(category));
}

/**
 * Can this role be taken away from the person modal?
 *
 * Only a LIVE clearance — a visitante or prestador arrangement. Narrow on purpose: revoking one
 * ends a visit arrangement, while the same button on a resident or staff role would cut somebody
 * out of their own home or workplace. That decision belongs to PermissionsTab, which warns about
 * the devices it will clear first.
 *
 * An id is required because the revoke is addressed by ROLE: a person holds several clearances at
 * once (Fernando Diniz holds three — Lote 185 twice and Lote 137 once) and revoking by person
 * would end whichever the backend found first.
 *
 * See personModalRules.test.ts.
 */
export function canRevokeRole(role: any): boolean {
  if (!role?.id) return false;
  if (role.is_active === false) return false;
  return role.role_type === 'visitor' || role.role_type === 'service_provider';
}

/**
 * Which KIND of clearance this role is — and nothing at all when it is not one.
 *
 * A card headed "PRESTADOR · Quadra UNIDADES – Lote 148" with a validity and a purpose reads as a
 * registration detail; what it actually is, is a liberação somebody granted, and which kind it is
 * changes what the porteiro does when the person turns up.
 *
 * The server's computed `liberacao.kind` answers when it is there; the metadata flags answer for
 * payloads that predate it — the same fallback utils/liberacoes keeps, and in the same order.
 */
export function liberacaoKindLabel(role: any): string | null {
  // Every label starts with "Liberação": the card is answering "what kind of clearance is this?",
  // and the old words ("Período liberado", "Sempre habilitado") read as a status the person has
  // rather than something somebody granted them.
  const kind = role?.liberacao?.kind;
  if (kind === 'sempre') return 'Liberação permanente';
  if (kind === 'grade') return 'Liberação por grade';
  if (kind === 'periodo') return 'Liberação por período';
  if (kind === 'visita_unica') return 'Liberação única';

  const meta = role?.role_metadata || {};
  if (meta.always_enabled === true) return 'Liberação permanente';
  if (Array.isArray(meta.visit_schedule) && meta.visit_schedule.length > 0) return 'Liberação por grade';
  if (meta.custom_period_access === true) return 'Liberação por período';
  if (meta.visitor_state === 'pre_registered') return 'Liberação única';
  // How the portaria's form records a one-off. Reading only `pre_registered` left those cards
  // with no kind at all, and a separate "Entradas: Entrada única" field said the same thing in a
  // second vocabulary.
  if (meta.access_type === 'single_visit') return 'Liberação única';

  // An ordinary visit registered at the gate. Not a liberação, and it must not claim to be one.
  return null;
}

/**
 * What to call the roles section.
 *
 * "Detalhes das funções" over three clearance cards does not say what they are. When every card
 * in the list IS a clearance, the section says so; a mixed list keeps the general heading, because
 * then it is not only about clearances.
 */
export function rolesSectionTitle(roles: any[] | null | undefined): string {
  const list = Array.isArray(roles) ? roles : [];
  if (list.length === 0) return 'Detalhes das funções';
  return list.every((role) => liberacaoKindLabel(role) !== null) ? 'Liberações' : 'Detalhes das funções';
}

/**
 * Is this value already printed somewhere else on the card?
 *
 * A prestador card headed "· Quadra UNIDADES – Lote 148" also printed "UNIDADE" and "UNIDADE
 * DESTINO" with the same text, and "EMPRESA" above "EMPRESA CONTRATADA" with the same company.
 * Three labels for one fact is harder to read than one.
 */
export function isDuplicateValue(value: string | null | undefined, alreadyShown: Array<string | null | undefined>): boolean {
  const normalise = (v: string | null | undefined) =>
    (v ?? '').trim().replace(/\s+/g, ' ').toLowerCase();

  const target = normalise(value);
  if (!target) return false;
  return alreadyShown.some((shown) => normalise(shown) === target);
}

/**
 * The unit a clearance is for, when the card can link to it.
 *
 * The in-card "Unidade" field used to be the clickable one and was dropped as a repeat of the
 * card's heading, so the heading carries the link now. Nothing when no unit is recorded — a dead
 * link is worse than plain text.
 */
export function roleUnitLink(role: any): string | null {
  const meta = role?.role_metadata || {};
  // Three places for one id. The modal's own top strip already reads `unit_id || unit_info.id`,
  // and reading only the flat key is what made the card render plain text while the strip showed
  // the SAME unit as a working link two inches above it.
  const candidates = [meta.unit_id, meta.unit_info?.id, meta.unit?.id];
  for (const candidate of candidates) {
    if (typeof candidate === 'string' && candidate.trim() !== '') return candidate;
  }
  return null;
}

/**
 * The resident who authorised a clearance, when the card can link to them.
 *
 * THREE keys for one idea, because three flows write it: the resident app's own liberação records
 * `authorized_by_resident_id`, the staff visitor form records `authorized_by_person_id`, and the
 * portaria's responsible-resident picker records `responsible_person_id`. The NAME shown on the
 * card already falls back this way, and so does the modal's top strip — if the link did not, half
 * the cards would be links and half plain text for no reason a reader could see.
 */
export function roleAuthorizedByLink(role: any): string | null {
  const meta = role?.role_metadata || {};
  for (const key of ['authorized_by_resident_id', 'authorized_by_person_id', 'responsible_person_id']) {
    const value = meta[key];
    if (typeof value === 'string' && value.trim() !== '') return value;
  }
  return null;
}

/**
 * The weekdays THIS clearance records — or nothing, when it records none.
 *
 * The card used to default to `[1,2,3,4,5]`, so every prestador showed a confident Seg–Sex strip
 * nobody had agreed to, indistinguishable from one where the resident really had picked those
 * five days. On a card the portaria reads before opening a gate, an invented restriction is worse
 * than a missing one: the operator cannot tell the two apart.
 *
 * Accepts the JSON-encoded form some write paths store, and drops anything that is not a weekday
 * rather than lighting up a day that does not exist.
 */
export function roleAllowedDays(role: any): number[] | null {
  const raw = role?.role_metadata?.allowed_days;

  let list: unknown[] | null = null;
  if (Array.isArray(raw)) {
    list = raw;
  } else if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) list = parsed;
    } catch {
      list = null;
    }
  }
  if (!list) return null;

  const days = list
    .map((value) => Number(value))
    .filter((value) => Number.isInteger(value) && value >= 0 && value <= 6);

  return days.length > 0 ? days : null;
}

/**
 * One entry, not a weekly pattern.
 *
 * "Entrada única" answers what the porteiro is actually asking — they come once, then it is spent
 * — where a row of weekday chips would imply a recurring arrangement.
 */
export function isSingleEntryRole(role: any): boolean {
  if (role?.liberacao?.kind === 'visita_unica') return true;
  return role?.role_metadata?.access_type === 'single_visit';
}

/**
 * When a clearance applies, said the way the rest of the product says it.
 *
 * Delegates to utils/liberacoes for anything that IS a clearance, because that is where the
 * sentence belongs and the modal kept getting it wrong by composing its own: a visita única is an
 * APPOINTMENT — one named day, often with the hour in `expected_arrival_at` — and printing
 * "A partir de 10/10/2026" told the porteiro the visitor may turn up any day from then on. Before
 * that, it printed `valid_from` and appended "até …" only when there was an end, so a start with
 * no end read as an expiry ("VÁLIDO 03/10/2026" on the 16th).
 *
 * The hand-rolled range below survives only for roles that are NOT clearances — a staff or
 * resident row, where there is no liberação to describe.
 */
export function roleValidityLabel(role: any, now: Date = new Date()): string | null {
  const summary = liberacaoSummaryForRole(role, now);
  if (summary?.rangeLabel) return summary.rangeLabel;

  // A one-off being USED right now: the shared walk no longer recognises it (the release moves
  // `visitor_state` off `pre_registered` and nothing durable is left), but the card still knows
  // what it is — and a one-off is a day, never "a partir de".
  if (isSingleEntryRole(role)) {
    const appointment = liberacaoAppointmentLabel(role);
    if (appointment) return appointment;
  }

  const from = parseLiberacaoDate(role?.valid_from);
  const until = parseLiberacaoDate(role?.valid_until);

  if (from && until) {
    const a = formatLiberacaoDate(role.valid_from);
    const b = formatLiberacaoDate(role.valid_until);
    // The resident picked a DAY; "03/10 até 03/10" says it twice and reads like a short period.
    return a === b ? a : `${a} até ${b}`;
  }
  if (from) return `A partir de ${formatLiberacaoDate(role.valid_from)}`;
  if (until) return `Até ${formatLiberacaoDate(role.valid_until)}`;
  return null;
}

function parseLiberacaoDate(value: unknown): Date | null {
  if (typeof value !== 'string' || value.trim() === '') return null;
  const parsed = new Date(value);
  return isNaN(parsed.getTime()) ? null : parsed;
}

const STATUS_LABELS: Record<string, string> = {
  liberado: 'Liberado',
  aguardando: 'Aguardando',
  vencida: 'Vencida',
  revogada: 'Revogada',
  encerrada: 'Encerrada',
};

/**
 * What the CLEARANCE is — not whether the database row is active.
 *
 * `is_active` is a flag: a visita única pre-registered on the 3rd and never used is still active
 * on the 16th, so the card said "● ATIVO" about access nobody has used. The porteiro reads this
 * badge before opening a gate.
 *
 * Three sources, in order of how much each actually knows:
 *
 *  1. `liberacaoStatusForRole` — the same walk the Liberados lists use, so the badge and the list
 *     cannot disagree about the clearance in front of them. It knows one thing the server does
 *     not: a visita única is over when its DAY is over, and a one-off usually carries no
 *     valid_until for the server to expire it on. Without this the card showed a green LIBERADO
 *     for a pré-cadastro from six weeks earlier that the list had already dropped.
 *  2. the server's computed status, for a role this walk does not recognise as a clearance.
 *  3. the row flag, for payloads that predate both.
 */
export function roleStatusLabel(role: any, now: Date = new Date()): string {
  const local = liberacaoStatusForRole(role, now);
  if (local) return STATUS_LABELS[local];

  const status = role?.liberacao?.status;
  if (typeof status === 'string' && STATUS_LABELS[status]) return STATUS_LABELS[status];
  return role?.is_active ? 'Ativo' : 'Inativo';
}

/** live = applies now · pending = arranged, not used yet · ended = over. */
export function roleStatusTone(role: any, now: Date = new Date()): 'live' | 'pending' | 'ended' {
  const status = liberacaoStatusForRole(role, now) ?? role?.liberacao?.status;
  if (status === 'aguardando') return 'pending';
  if (status === 'vencida' || status === 'revogada' || status === 'encerrada') return 'ended';
  if (status === 'liberado') return 'live';
  return role?.is_active ? 'live' : 'ended';
}
